import { apiGet, apiPost } from '../../../shared/api-client/index.js';
import { entityListKey, type EntityListKey } from '../../../shared/api/entityQueryKeys.js';

export type RunStatus =
  | 'queued'
  | 'running'
  | 'awaiting_approval'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type RunOutcome =
  | 'queued'
  | 'in_progress'
  | 'waiting_approval'
  | 'follow_up_queued'
  | 'skipped'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'unknown';

export interface RunCenterDealRef {
  id: string;
  title: string;
}

export interface RunCenterContactRef {
  id: string;
  name: string;
}

export interface RunCenterScheduledOccurrence {
  localDate: string;
  localTime: string;
  timezone: string;
}

export interface RunCenterRunSummary {
  kind: 'my_day';
  hotLeads: number;
  overdueTasks: number;
  riskDeals: number;
  todayMeetings: number;
  truncated: boolean;
}

/** Safe journal row as produced by the backend read model. */
export interface RunCenterRun {
  id: string;
  scenarioId: string;
  scenarioName: string;
  /** Stable semantic key equal to Scenario.config.id; null only when no usable id. Unknown keys allowed. */
  presetKey: string | null;
  status: RunStatus;
  attempts: number;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  nextAttemptAt: string | null;
  deal: RunCenterDealRef | null;
  contact: RunCenterContactRef | null;
  outcome: RunOutcome;
  pendingApprovalId: string | null;
  /** Scheduled occurrence slot; null for event/manual runs. */
  scheduledOccurrence: RunCenterScheduledOccurrence | null;
  /** Bounded briefing summary; null for every other run. */
  summary: RunCenterRunSummary | null;
}

export interface RunCenterScenarioFacet {
  id: string;
  name: string;
  /** Stable semantic key equal to Scenario.config.id; null only when no usable id. Unknown keys allowed. */
  presetKey: string | null;
}

export interface RunCenterPage {
  runs: RunCenterRun[];
  scenarios: RunCenterScenarioFacet[];
}

export interface ListRunCenterFilters {
  status?: RunStatus;
  scenarioId?: string;
  /**
   * Backend window size (1..200). The KPI overview passes
   * RUN_CENTER_OVERVIEW_LIMIT explicitly so the counters read the widest
   * window; the journal list keeps the backend default (50).
   */
  limit?: number;
}

/** Widest backend window for journal-wide KPI counters. */
export const RUN_CENTER_OVERVIEW_LIMIT = 200;

export function getRunCenterQueryKey(
  workspaceId: string,
  filters: ListRunCenterFilters = {},
): EntityListKey {
  return entityListKey('run-center', workspaceId, { ...filters });
}

export async function listRunCenterRuns(
  filters: ListRunCenterFilters = {},
): Promise<RunCenterPage> {
  const search = new URLSearchParams();
  if (filters.status !== undefined) {
    search.set('status', filters.status);
  }
  if (filters.scenarioId !== undefined) {
    search.set('scenarioId', filters.scenarioId);
  }
  // Without this the KPI overview's explicit window silently fell back to
  // the backend default (50) instead of reading the widest one.
  if (filters.limit !== undefined) {
    search.set('limit', String(filters.limit));
  }
  const qs = search.toString();
  return apiGet<RunCenterPage>(
    qs.length > 0 ? `/scenario-runs/run-center?${qs}` : '/scenario-runs/run-center',
  );
}

export interface RunCenterMutationResult {
  id: string;
  status: RunStatus;
}

export async function cancelRunCenterRun(id: string): Promise<RunCenterMutationResult> {
  return apiPost<RunCenterMutationResult, Record<string, never>>(
    `/scenario-runs/${encodeURIComponent(id)}/cancel`,
    {},
  );
}

export async function retryRunCenterRun(id: string): Promise<RunCenterMutationResult> {
  return apiPost<RunCenterMutationResult, Record<string, never>>(
    `/scenario-runs/${encodeURIComponent(id)}/retry`,
    {},
  );
}
